/** @module csi/view/PdfController */
define([
	"dojo/_base/declare",
	"dojo/promise/all",
	"csi",
	"csi/action/ActionView",
	"csi/api",
	"csi/comm",
	"csi/comm/PostRequest",
	"csi/def",
	"csi/locale",
	"csi/reflection",
	"csi/resolver",
	"csi/session",
	"csi/state",
	"csi/state/rootStore",
	"csi/utility/stringUtility",
	"csi/view",
	"csi/view/PdfRequestController",
	"csi/view/PrintService",
	"csi/widget/MessageDialog",
	"csi/xml",
	"csi/xml/Writer"
], function(declare, all, csi, ActionView, api, comm, PostRequest, def, locale, reflection, resolver, session, state, {sessionStore}, {stringCombine}, csiView, PdfRequestController, PrintService, MessageDialog, xml, Writer) {

	var DOCUMENT = "Document";

	/**
	 * Encode name so that it is acceptable to show as browser title. Some characters are disallowed in entire string,
	 * whereas a period cannot appear at the end. There may be more special cases, but these are the ones found so far.
	 * @param {string} name
	 * @return {string}
	 */
	function _encodeName(name) {
		return name.replace(/[\[\]\\\/\?\%\*\&\:\|\<\>\"]/g, "").replace(/\.$/, "");
	}

	return declare(PdfRequestController, {

		PROP_CLIENT_START_RENDERING_MSUTC: "ClientStartRenderingMSUTC",

		PROP_CLIENT_RENDERING_TIME: "ClientRenderingTime",

		PROP_TOTAL_TIME: "TotalTime",

		/**
		 * {Date object} Client start rendering time
		 */
		clientStartRendering: null,

		/**
		 * {number} Client rendering time in ms
		 *
		 * Time to render client HTML for PDF generation
		 */
		clientRenderingTime: 0,

		/**
		 * {number} Overall PDF generation time in ms
		 */
		totalTime: 0,

		// Boolean: indicates if Pdf should be saved as Document as well
		//bDocument: false,

		// Boolean: indicates if view being printed is designated as the print view - if true, then do not print header and do prompt for locale
		//bPrintView: false,

		// Boolean: indicates if link to Pdf file should be saved
		//bRecordPdf: false,

		// Boolean: determine if action being invoked from item in view or from view toolbar
		bRefNode: true,

		// String: name for file
		//filename: null,

		// String: url of existing PDF file
		//urlFile: null,

		// String: view to print to pdf file, by default it is the current view
		//viewId: null,

		// String: url to file with Html content
		//_urlHtmlFile: null,

		// String: url to view Html content
		//_urlLocation: null,

		confirmAction: function(/*Object*/params) {
			// summary: give user option to save PDF (EXPORT), Save replacing existing (EXPORT_REPLACE), or cancel
			params.buttons = ["EXPORT" + MessageDialog.BUTTON_PRIMARY, "EXPORT_REPLACE", "C"];
			this.inherited(arguments);
		},

		getActionView: function(/*ViewRef?*/vr) {
			// summary: popup document form if so configured
			if (this.bDocument) {
				var rc = reflection.getClass(DOCUMENT);
				var defView = def.getClassViewDef(rc, "NewDocument");
				return new ActionView(this, defView);
			}

			return this.inherited(arguments);
		},

		getFormData: function() {
			// summary: if creating document, then remove reference to current node (not an edit form)
			var data = this.inherited(arguments);

			if (this.bDocument) {
				data.m_node = null;
			}

			return data;
		},

		hasToConfirmAction: function() {
			// summary: return true if node is derived from DataSheetRevision and revision is approved
			var node = this.getNode();
			var rc = reflection.getNodeClass(node);

			return this.bPrintView &&
				rc.isDerivedFrom("DataSheetRevision") &&
				xml.nodeHasRight(node, xml.RIGHT_WRITE) &&
				(node[reflection.ATTR_STATE] == reflection.REVISION_APPROVED);
		},

		onConfirm: function(/*String*/key) {
			// summary: skip to end if operation cancelled; otherwise continue with processing - if no selected,
			// then do not overwrite existing PDF file with the newly exported one
			if (key == "C") {
				this.postProcess();
			} else {
				if (key == "EXPORT_REPLACE") {
					this.bRecordPdf = true;
				}

				this.preProcess();
			}
		},

		onCreatePopup: function(/*csi.view.Widget*/view) {
			// summary: initialize fields from user settings
			view.addDefaultValue(xml.ATTR_NAME, this._getInitialName() || "");
			this.inherited(arguments);
		},

		preProcess: function() {
			// summary: show error message and stop action if already printing
			// FIX: Potential fix would be to make PrintService watch print state and only start printing once state changes to false;
			// not sure if we want to allow user to start a lot of prints all at once (for example, print all quotes in table one by one)
			if (state.get("print")) {
				this.displayErrorDialog({message: locale.resource("ErrorPrintBusy")});
			} else {
				this.inherited(arguments);
			}
		},

		process: function() {
			// summary: create URL to hold resulting PDF file
			var self = this;
			var dfdContent;
			var name = (this.m_formData && this.m_formData.getFieldValue(xml.ATTR_NAME)) || this._getInitialName();

			this.fileName = _encodeName(name);
			this.setProgress();

			// set state indicating client is in process of ad-hoc printing
			state.set("printAdHoc", true);
			this.clientStartRendering = new Date();
			if (this.refNode) {
				dfdContent = PrintService.printViewOnNode(this.getNode(), this.viewId, null, true);
			} else {
				dfdContent = PrintService.printCurrentView(this.widget, true);
			}

			var dfd = all({
				html: dfdContent,
				file: comm.createFileUrl("content.html")
			});

			this.when(dfd, function(result) {
				// C8-6316
				// determine client rendering time and store the ms in member variable for later setting
				var curTime = new Date();
				self.clientRenderingTime = curTime.valueOf() - self.clientStartRendering.valueOf();
				self._urlHtmlFile = result.file.getFileUrl();

				// call api to modify HTML content before publishing to server - this is needed in some cases
				// where a portal is used to access the network, and client URLs may not match server URLs
				var htmlContent = result.html.content;
				var f = api.getExternalToInternalUrlConverter();
				if (f) {
					htmlContent = f(htmlContent);
				}

				self._urlLocation = csi.url("fragment.html?Locale=" + sessionStore.locale + session.getLocation(null, self._getLocationParams(result.html.paper, result.html.orientation)), true);

				// upload HTML file - use static filename as it is never visible to user, and using Node Name can possibly introduce
				// high-bit characters into filename that is not handled by IIS.
				const req = new PostRequest("SiteAdmin", "CreateAdHocPDFRequest")
					.addParameter("Priority", 1)
					.addParameter("FN", "content.html")
					.addParameter("HtmlURL", self._urlHtmlFile)
					.addParameter("FileName", self.fileName)
					.addParameter("Location", self._urlLocation)
					.setBinary("FL", htmlContent);

				// don't add header for print views because the titleblock and properties should already give enough context
				if (!self.bPrintView) {
					req.addParameter("Header", PrintService.getPrintTitle(self.widget));
				}

				// add request to PDFRequestContainer if one exists
				if (self.getNode().PDFRequests) {
					req.addParameter("PDFRequestContainer", self.m_urlNode);
				}

				if (self.bRecordPdf) {
					req.addParameter("RecordPDF", true);
				}

				self.waitForPublish(req.post());
			});
		},

		onPublishResponse(response) {
			const pdfRequest = response.getResultNode();
			this.urlFile = pdfRequest.File;
			this.urlRequest = pdfRequest.$URL;
		},

		_getInitialName: function() {
			// summary: return filename for PDF file
			const {cacheView, defView} = this.widget;
			const viewName = defView.getId() === def.getHostViewId() ? "" : defView.getName();
			let node = this.getNode();

			if (reflection.isRevisionNode(node)) {
				node = resolver.resolveParent(node, cacheView);
			}

			node = node || routerStore.locationNode;

			return stringCombine(node.$Name || locale.type(node.$Type), viewName, " - ");
		},

		/**
		 * @param {string} - paper
		 * @param {string} - orientation
		 */
		_getLocationParams: function(paper, orientation) {
			// summary: return href to print view - the file is really the only parameter that is necessary, but the rest is just for informational purposes
			var params = {};
			var urlCustomView = this.refNode ? null : this.widget.get("customViewId");
			if (urlCustomView) {
				params[session.LOCATION_CUSTOM_VIEW] = urlCustomView;
			}

			params[session.LOCATION_HTML_FILE] = this._urlHtmlFile;
			params[session.LOCATION_ORIENTATION] = orientation;
			params[session.LOCATION_PAPER] = paper;
			params[session.LOCATION_URL] = this.m_urlNode;
			params[session.LOCATION_VIEW] = this.viewId;

			return params;
		},

		_postProcessComplete: function(/*Node*/pdfRequest, /*Boolean*/bStatus) {
			// summary: called when _postProcessPdf detects pdf is complete
			this.widget.postAction(this);
			this.inherited(arguments);

			// C8-6316
			// Determine total processing time to generate PDF
			if (this.clientStartRendering != null) {
				var curTime = new Date();
				this.totalTime = curTime.valueOf() - this.clientStartRendering.valueOf();
				this.widget.writeOperations((w) => {
					// call server to store timings
					w.callMethod("SiteAdmin", "UpdatePDFRequestTimings")
						.addParameter(xml.PROP_URL, xml.ATTR_REF, pdfRequest.$URL)
						.addParameter(this.PROP_CLIENT_START_RENDERING_MSUTC, xml.ATTR_LONG, this.clientStartRendering.valueOf())
						.addParameter(this.PROP_CLIENT_RENDERING_TIME, xml.ATTR_INTEGER, this.clientRenderingTime)
						.addParameter(this.PROP_TOTAL_TIME, xml.ATTR_INTEGER, this.totalTime);
				});
			}

			// create document and attach to node if so configured
			if (bStatus && this.bDocument) {
				var node = this.getNode();
				var url = comm.createUrl();

				var w = new Writer();

				w.createNode(url, DOCUMENT, this.m_formData.getFieldValue(xml.ATTR_NAME));
				w.changeNode(xml.getUrlPath(url, reflection.PATH_CURRENT_REVISION)).changeAttribute("File", xml.ATTR_URL, this.urlFile);

				if (node.DocumentsAndComments) {
					w.changeParent(node.DocumentsAndComments, "Documents", xml.ATTR_REFLIST, [url], true);
				}

				w.execute({sync: true});

				// update documents view if present
				var view = csiView.getView("Documents");
				if (view && view.isValidView()) {
					view.refreshWidget();
				}
			}
		},

		_setRefNode: function(/*DOMNode*/refNode) {
			// summary: if printing view, then initialize properties; otherwise leave to derived class to do so
			if (refNode) {
				// __Print__ is default print view for nodes
				this.bPrintView = true;
				this.viewId = "__Print__";
			} else {
				// action may be invoked from slim view, so make sure to act on root view
				var view = this.widget.getToolbarContainerView();
				if (view && !view.toolbarNode) {
					this.widget = view;
				} else {
					view = this.widget;
				}

				this.bPrintView = view.bPrintView;
				this.viewId = view.defView.getId();
				this.m_urlNode = view.getViewURL();
			}

			this.inherited(arguments);
		}
	});
});
